import { cn } from "@/lib/cn";
import { DURATION, EASE, TRANSITION } from "@/lib/motion";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, useReducedMotion } from "motion/react";
import { forwardRef } from "react";

const FILL_TONES = {
  primary: "bg-primary",
  secondary: "bg-secondary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
} as const;

/** Classes of the `ProgressBar` track. */
export const progressTrackVariants = cva("w-full overflow-hidden rounded-full bg-gray-scale-200", {
  variants: {
    size: { xs: "h-1", sm: "h-1.5", md: "h-2", lg: "h-3" },
  },
  defaultVariants: { size: "md" },
});

type ProgressTone = keyof typeof FILL_TONES;

/** Props of `ProgressBar`; `value` is a 0..1 fraction (clamped). */
export type ProgressBarProps = VariantProps<typeof progressTrackVariants> & {
  value: number;
  tone?: ProgressTone;
  label?: string;
  valueText?: string;
  className?: string;
};

function clampFraction(value: number) {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

/** Determinate progress bar whose fill animates with `TRANSITION.progress`. */
export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
  { value, tone = "primary", size, label, valueText, className },
  ref,
) {
  const percent = Math.round(clampFraction(value) * 100);
  return (
    <div
      ref={ref}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-valuetext={valueText}
      className={cn(progressTrackVariants({ size }), className)}
    >
      <motion.div
        className={cn("h-full rounded-full", FILL_TONES[tone])}
        initial={false}
        animate={{ width: `${percent}%` }}
        transition={TRANSITION.progress}
      />
    </div>
  );
});

/** Props of `SegmentedProgress`; `current` is the zero-based active segment. */
export type SegmentedProgressProps = VariantProps<typeof progressTrackVariants> & {
  segments: number;
  current: number;
  tone?: ProgressTone;
  variant?: "bars" | "dots";
  pulseCurrent?: boolean;
  label?: string;
  className?: string;
};

const SEGMENT_HEIGHT = { xs: "h-1", sm: "h-1.5", md: "h-2", lg: "h-3" } as const;

const PULSE = {
  opacity: [1, 0.45, 1],
  transition: { duration: DURATION.emphasis * 3, ease: EASE.standard, repeat: Infinity },
};

/** Step indicator: equal bars, or dots where the current step stretches into a pill. */
export const SegmentedProgress = forwardRef<HTMLDivElement, SegmentedProgressProps>(function SegmentedProgress(
  { segments, current, tone = "primary", size, variant = "bars", pulseCurrent = false, label, className },
  ref,
) {
  const reduceMotion = useReducedMotion();
  const total = Math.max(1, Math.floor(segments));
  const active = Math.min(total - 1, Math.max(0, current));
  const isDots = variant === "dots";

  return (
    <div
      ref={ref}
      role="progressbar"
      aria-label={label}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={active + 1}
      className={cn("flex w-full items-center", isDots ? "justify-center gap-2" : "gap-1", className)}
    >
      {Array.from({ length: total }, (_, index) => {
        const state = index < active ? "done" : index === active ? "current" : "upcoming";
        return (
          <motion.span
            key={index}
            aria-hidden
            data-state={state}
            className={cn(
              "block rounded-full transition-[width,background-color] duration-slow ease-standard",
              SEGMENT_HEIGHT[size ?? "md"],
              isDots ? (state === "current" ? "w-8" : "w-2") : "flex-1",
              state === "upcoming" ? (isDots ? "bg-card-text/20" : "bg-gray-scale-300") : FILL_TONES[tone],
            )}
            initial={false}
            animate={
              pulseCurrent && state === "current" && !reduceMotion
                ? PULSE
                : { opacity: state === "done" && isDots ? 0.5 : 1, transition: TRANSITION.fade }
            }
          />
        );
      })}
    </div>
  );
});
